﻿import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Award,
  Mail,
  BarChart3,
  Settings,
  LogOut,
  Vote,
  ExternalLink,
  Menu,
  X,
} from 'lucide-react';

interface AdminSidebarProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  voterCount?: number;
  electionStatus?: string;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  currentTab,
  onTabChange,
  voterCount,
  electionStatus = 'active',
}) => {
  const { admin, logoutAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'voters', label: 'Accredited Voters', icon: Users, badge: voterCount },
    { id: 'candidates', label: 'Candidate Details', icon: Award },
    { id: 'broadcast', label: 'Emails Broadcast', icon: Mail },
    { id: 'analytics', label: 'Demographic Analysis', icon: BarChart3 },
    { id: 'settings', label: 'Election Settings', icon: Settings },
  ];

  return (
    <>
      {/* Desktop Persistent Left Sidebar (>= 1001px) */}
      <aside className="admin-sidebar-desktop">
        <div>
          {/* Header / Brand */}
          <div style={{ marginBottom: '32px', paddingLeft: '8px' }}>
            <Link to="/" style={{ textDecoration: 'none', display: 'block' }}>
              <img
                src="/assets/images/electpoll logo (plain white).png"
                alt="Electpoll"
                style={{ height: '34px', display: 'block' }}
              />
            </Link>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: '#FF9900',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                }}
              >
                Admin Console
              </span>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  backgroundColor:
                    electionStatus === 'active'
                      ? 'rgba(16, 185, 129, 0.2)'
                      : electionStatus === 'paused'
                      ? 'rgba(245, 158, 11, 0.2)'
                      : 'rgba(107, 114, 128, 0.2)',
                  color:
                    electionStatus === 'active'
                      ? '#10B981'
                      : electionStatus === 'paused'
                      ? '#F59E0B'
                      : '#9CA3AF',
                  textTransform: 'uppercase',
                }}
              >
                {electionStatus}
              </span>
            </div>
          </div>

          {/* 6 Requested Admin Navigation Items */}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onTabChange(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '11px 14px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#FF9900' : 'transparent',
                    color: isActive ? '#171619' : '#D1D5DB',
                    border: 'none',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '13.5px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Icon size={17} color={isActive ? '#171619' : '#FF9900'} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '2px 7px',
                        borderRadius: '10px',
                        backgroundColor: isActive ? 'rgba(0,0,0,0.15)' : '#2D2C31',
                        color: isActive ? '#171619' : '#A1A1AA',
                        fontWeight: 700,
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Quick link to live public results */}
            <Link
              to="/results"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '11px 14px',
                borderRadius: '8px',
                color: '#9CA3AF',
                fontWeight: 500,
                fontSize: '13px',
                marginTop: '12px',
                border: '1px dashed #2D2C31',
                textDecoration: 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Vote size={16} color="#A1A1AA" />
                <span>Public Live Results</span>
              </div>
              <ExternalLink size={13} color="#6B7280" />
            </Link>
          </nav>
        </div>

        {/* Admin User & Logout */}
        <div style={{ paddingTop: '16px', borderTop: '1px solid #2D2C31' }}>
          <div style={{ marginBottom: '12px', paddingLeft: '4px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#FFFFFF' }}>
              {admin?.name || 'Electoral Commissioner'}
            </div>
            <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '2px' }}>
              {admin?.email || 'admin@electpoll.com'}
            </div>
          </div>

          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '9px 12px',
              borderRadius: '6px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#EF4444',
              fontWeight: 600,
              fontSize: '12.5px',
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
          >
            <LogOut size={15} />
            <span>Exit Admin Session</span>
          </button>
        </div>
      </aside>

      {/* Mobile Top Header (<= 1000px) */}
      <header className="admin-header-mobile">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/assets/images/electpoll logo (plain white).png"
              alt="Electpoll"
              style={{ height: '26px' }}
            />
          </Link>
          <span
            style={{
              fontSize: '10px',
              color: '#FF9900',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              backgroundColor: 'rgba(255,153,0,0.12)',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            Admin
          </span>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle admin menu"
          style={{
            background: 'none',
            border: 'none',
            color: '#FFFFFF',
            cursor: 'pointer',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </header>

      {/* Mobile Admin Drawer */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#171619',
            padding: '20px 16px',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
        >
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onTabChange(item.id);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? '#FF9900' : 'rgba(255,255,255,0.04)',
                    color: isActive ? '#171619' : '#D1D5DB',
                    border: 'none',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Icon size={18} color={isActive ? '#171619' : '#FF9900'} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        backgroundColor: isActive ? 'rgba(0,0,0,0.2)' : '#2D2C31',
                        color: isActive ? '#171619' : '#A1A1AA',
                        fontWeight: 700,
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}

            <Link
              to="/results"
              target="_blank"
              rel="noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                borderRadius: '8px',
                color: '#9CA3AF',
                fontWeight: 500,
                fontSize: '13px',
                marginTop: '8px',
                border: '1px dashed #2D2C31',
                textDecoration: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Vote size={16} color="#A1A1AA" />
                <span>Public Live Results</span>
              </div>
              <ExternalLink size={13} color="#6B7280" />
            </Link>
          </nav>

          <div style={{ paddingTop: '16px', borderTop: '1px solid #2D2C31', marginTop: '20px' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#FFFFFF' }}>
                {admin?.name || 'Electoral Commissioner'}
              </div>
              <div style={{ fontSize: '11px', color: '#8E8E93', marginTop: '2px' }}>
                {admin?.email || 'admin@electpoll.com'}
              </div>
            </div>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '11px 14px',
                borderRadius: '6px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#EF4444',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              <LogOut size={16} />
              <span>Exit Admin Session</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
